/**
 * Donors by Partner Unit — the giving register turned the other way up.
 *
 * The Donors page answers "what does this person support": a donor, with their units.
 * This answers the other question — "who supports this unit" — off the same 962 links,
 * and neither is a sort of the other, so it has its own shape rather than that list with
 * a different ORDER BY.
 *
 * Units with nobody giving to them are part of the answer rather than the absence of one.
 * A report like this is opened to find those as often as to read the others, which is why
 * "no donors recorded" is a filter of its own and not something to scroll past.
 *
 * Owners and administrators, like everything else that reads the register: a donor's name
 * beside the unit they give to is the most personal join in the archive.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi } from '../lib/api'
import {
  Card, Tile, Badge, Empty, Notice, QueryState, SearchInput, Tabs, AlumniToggle,
  CountryLabel, fmtInt, downloadCsv,
} from '../components/ui'
import { useRelabel, useCode } from '../lib/terms'

/** Which half of the register to look at. Three views, one of them everything. */
const VIEWS = [
  { key: '', label: 'All partner units' },
  { key: 'with', label: 'With donors' },
  { key: 'without', label: 'No donors recorded' },
]

/** A donor's name for a row, falling back to what identifies them. */
const donorName = (d) => d.preferred_name || d.email || '—'

export default function DonorsByPartner({ user }) {
  const code = useCode()
  const t = useRelabel()
  const [q, setQ] = useState('')
  const [view, setView] = useState('')
  const [withAlumni, setWithAlumni] = useState(false)
  /* Which units are showing their donors. A Set rather than one open row: comparing two
     units side by side is the point of a report, and an accordion would forbid it. */
  const [open, setOpen] = useState(() => new Set())

  const allowed = user.caps.manageSettings
  const query = useApi(
    ['donors-by-partner', q, view, withAlumni],
    `donor-registry/by-partner?${new URLSearchParams({
      ...(q ? { q } : {}),
      ...(view ? { only: view } : {}),
      ...(withAlumni ? { include_alumni: '1' } : {}),
      limit: '500',
    })}`,
    { enabled: allowed, keepPreviousData: true }
  )

  if (!allowed) {
    return (
      <PageHeader title={t('Donors by Partner Unit')}>
        <Notice tone="warn" icon="lock" title="Not available to your role">
          This report puts a donor&rsquo;s name beside the {t('partner units')} they give to,
          which is the most personal join in the archive, so it is kept to owners and
          administrators. You are signed in as <strong>{user.roleLabel}</strong>.
        </Notice>
      </PageHeader>
    )
  }

  /* One line per donor–unit pair rather than per unit: a cell holding forty names is not
     something a spreadsheet can sort. Every unit the report holds, not only those open. */
  const exportCsv = () =>
    downloadCsv(
      'donors-by-partner.csv',
      (query.data?.rows ?? []).flatMap((r) =>
        r.donors.length
          ? r.donors.map((x) => ({
              s4ca_code: r.s4ca_code,
              display_name: r.display_name,
              country_name: r.country_name,
              status: r.status,
              donor_count: r.donor_count,
              donor: donorName(x),
              donor_email: x.email ?? '',
              campaign: x.campaign,
            }))
          : [
              {
                s4ca_code: r.s4ca_code,
                display_name: r.display_name,
                country_name: r.country_name,
                status: r.status,
                donor_count: 0,
                donor: '',
                donor_email: '',
                campaign: '',
              },
            ]
      ),
      [
        { key: 's4ca_code', label: 'S4CA Code' },
        { key: 'display_name', label: 'Partner Unit' },
        { key: 'country_name', label: 'Country' },
        { key: 'status', label: 'Status' },
        { key: 'donor_count', label: 'Donors' },
        { key: 'donor', label: 'Donor' },
        { key: 'donor_email', label: 'Donor email' },
        { key: 'campaign', label: 'Campaign' },
      ]
    )

  const toggle = (id) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <PageHeader
      title={t('Donors by Partner Unit')}
      sub={t('Which partner units are supported, and by whom')}
      actions={
        <button className="btn btn-sm" onClick={exportCsv} disabled={!query.data?.rows?.length}>
          ↓ CSV
        </button>
      }
    >
      <QueryState query={query}>
        {(d) => {
          const rows = d.rows
          const supported = d.totals.units_supported
          return (
            <div className="col">
              <div className="grid grid-tiles">
                <Tile
                  label={t('Partner units supported')}
                  value={`${fmtInt(supported)} of ${fmtInt(d.totals.units)}`}
                  note={`${fmtInt(d.totals.units - supported)} with no donor recorded`}
                />
                <Tile label="Donors in the register" value={fmtInt(d.totals.donors)} />
                <Tile
                  label="Donor–unit links"
                  value={fmtInt(d.totals.links)}
                  note={
                    supported
                      ? `${(d.totals.links / supported).toFixed(1)} donors per supported unit`
                      : undefined
                  }
                />
              </div>

              {!d.totals.links && (
                <Notice icon="info" title="The register is empty">
                  Nothing has been imported yet.{' '}
                  <Link to="/settings/donor-import">Import Donors</Link> reads a giving report
                  and fills this report from it.
                </Notice>
              )}

              {/* The same controls every list carries: the search box on its own line, then
                  the view tabs and the alumni switch. */}
              <SearchInput value={q} onChange={setQ} placeholder={t('Unit or donor…')} aria-label="Search" />
              <div className="row wrap">
                <Tabs tabs={VIEWS} active={view} onChange={setView} />
                <AlumniToggle checked={withAlumni} onChange={setWithAlumni} />
              </div>

              {!rows.length ? (
                <Empty title="Nothing to show">
                  {q
                    ? t('No partner unit matches that, and no donor giving to one does either.')
                    : t('No partner unit matches this view.')}
                </Empty>
              ) : (
                <Card
                  title={`${fmtInt(d.total)} ${t(d.total === 1 ? 'Partner Unit' : 'Partner Units')}`}
                  sub={
                    rows.length < d.total
                      ? `showing the first ${fmtInt(rows.length)}, most supported first`
                      : 'most supported first'
                  }
                >
                  <div className="table-wrap">
                    <table className="data compact">
                      <thead>
                        <tr>
                          <th>{t('Partner unit')}</th>
                          <th>Country</th>
                          <th className="right">Donors</th>
                          <th>Giving to it</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => {
                          const isOpen = open.has(r.partner_id)
                          /* Collapsed to the first few names with a count, because a unit
                             with 48 donors would otherwise be the whole screen. */
                          const shown = isOpen ? r.donors : r.donors.slice(0, 4)
                          return (
                            <tr key={r.partner_id}>
                              <td className="small">
                                <Link to={`/partners/${r.partner_id}`} className="mono">
                                  {code(r.s4ca_code)}
                                </Link>{' '}
                                {code(r.display_name)}
                                {r.status === 'alumni' && (
                                  <>
                                    {' '}
                                    <Badge tone="muted">alumni</Badge>
                                  </>
                                )}
                              </td>
                              <td className="small">
                                <CountryLabel name={r.country_name} flag={r.flag_emoji} />
                              </td>
                              <td className="num">{fmtInt(r.donor_count)}</td>
                              <td className="small">
                                {!r.donors.length ? (
                                  <span className="subtle">no donors recorded</span>
                                ) : (
                                  <div className="row wrap" style={{ gap: 6 }}>
                                    {shown.map((x, i) => (
                                      <span
                                        key={`${x.donor_id}`}
                                        title={`${x.email ?? 'no address on file'} — ${x.campaign}`}
                                      >
                                        {i > 0 && (
                                          <span className="subtle" aria-hidden="true">
                                            ·{' '}
                                          </span>
                                        )}
                                        {donorName(x)}
                                      </span>
                                    ))}
                                    {r.donors.length > 4 && (
                                      <button
                                        className="btn btn-sm btn-ghost"
                                        onClick={() => toggle(r.partner_id)}
                                      >
                                        {isOpen
                                          ? 'show fewer'
                                          : `+${fmtInt(r.donors.length - 4)} more`}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>
          )
        }}
      </QueryState>
    </PageHeader>
  )
}
